/**
 * API keys — the credential this RMS issues so intake can address it as a
 * Backend (ADR-0016).
 *
 * The whole surface exists because a Backend is one rental system among
 * several and every other one hands an integrator a key: pasting one into
 * a form is how a Company connects Current RMS, and until this row existed
 * connecting the HireOS RMS was a CLI run against two environment
 * variables instead.
 *
 * The secret is shown exactly once, on the row that just minted it. The
 * server keeps a SHA-256 digest, so there is no second chance to fetch it
 * and no "reveal" control to build — losing it means minting another,
 * which is the intended cost of never being able to leak it from here.
 *
 * The group owns its own header, so the drilled-into space has one
 * heading rather than the same words twice. Minting follows the house
 * create-form shape (Suppliers, Vehicles): an action in that header
 * opening a panel below the list it appends to, with its buttons on their
 * own row — a submit button parked beside a labelled input never lines up
 * with it, and does not need to.
 *
 * Revoking is instant and one-way, so it confirms (DESIGN-SYSTEM.md: destructive
 * actions always confirm). A revoked key then leaves the list: nothing can
 * be done with it here, and the screen is a list of what a caller can
 * still reach this workspace with. The row stays in the table.
 */
import { useEffect, useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { CopyIcon } from "../../components/ui/icons";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { useSuccessToast } from "../../components/ui/Toast";
import forms from "../../components/ui/field.module.css";
import { cx } from "@shared/cx";
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
} from "../../lib/api-keys-api";
import { formatTimestamp } from "../../lib/format-utils";
import { errorMessage } from "../../lib/use-mutation-runner";
import { Loading, SectionHeader } from "./section-chrome";
import badge from "../../lib/status-badge.module.css";
import chrome from "./settings.module.css";
import styles from "./ApiKeysGroup.module.css";

const DESCRIPTION =
  "A key lets HireOS intake write phone and form enquiries into this workspace as priced quotes. Paste it into intake once; it is not shown again.";

export function ApiKeysGroup() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // The plaintext key, held in component state alone for as long as the
  // Operator stays on this screen. Navigating away is what discards it.
  const [minted, setMinted] = useState<string | null>(null);
  const toast = useSuccessToast();
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const load = (signal?: AbortSignal) =>
    listApiKeys(signal)
      .then((rows) => {
        setKeys(rows);
        setLoadError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setLoadError(errorMessage(err));
      });

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = () => {
    if (!minted) return;
    void navigator.clipboard
      .writeText(minted)
      .then(() => toast("API key copied."));
  };

  const create = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const res = await createApiKey(name.trim());
      setMinted(res.key);
      setName("");
      setFormOpen(false);
      await load();
    } catch (err) {
      setCreateError(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const doRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeApiKey(confirmRevoke.id);
      setConfirmRevoke(null);
      await load();
    } catch (err) {
      setRevokeError(errorMessage(err));
    } finally {
      setRevoking(false);
    }
  };

  if (!keys) {
    return (
      <>
        <SectionHeader title="API keys" description={DESCRIPTION} />
        {loadError ? <Banner variant="error">{loadError}</Banner> : <Loading />}
      </>
    );
  }

  return (
    <>
      <SectionHeader
        title="API keys"
        description={DESCRIPTION}
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setCreateError(null);
              setFormOpen(true);
            }}
          >
            Create key
          </Button>
        }
      />

      {loadError && (
        <Banner
          variant="error"
          className={chrome.banner}
          onDismiss={() => setLoadError(null)}
        >
          {loadError}
        </Banner>
      )}

      {/* Ephemeral dismiss: the key is gone from the server the moment it
          is minted, so the X only clears it from this screen — which is
          also the only place it has ever been. A warning, not a success:
          the key still needs copying, and there is no success banner
          (DESIGN-SYSTEM.md, Banners). */}
      {minted && (
        <Banner
          variant="warning"
          className={chrome.banner}
          onDismiss={() => setMinted(null)}
        >
          <span className={styles.mintedLead}>
            Copy this key now. It is not shown again.
          </span>
          <span className={styles.mintedLine}>
            {/* Truncated, not wrapped: a credential to hand over, never
                something to read. The full value stays in the DOM so copy
                gives the whole key. */}
            <code className={styles.keyValue}>
              {minted}
            </code>
            <button
              type="button"
              aria-label="Copy API key"
              onClick={copy}
              className={chrome.copyButton}
            >
              <CopyIcon className={chrome.icon} />
            </button>
          </span>
        </Banner>
      )}

      <ul className={chrome.listBordered}>
        {keys.length === 0 && (
          <li className={styles.emptyRow}>
            No keys yet. Create one to connect intake.
          </li>
        )}
        {keys.map((k) => (
          <li key={k.id} className={cx(chrome.row, chrome.rowTall)}>
            <div className={chrome.rowText}>
              <p className={chrome.rowTitle}>
                {k.name || "Untitled key"}
              </p>
              <p className={chrome.rowMetaTight}>
                <code className={styles.prefix}>{k.prefix}…</code>
                {k.last_used_at
                  ? ` · last used ${formatTimestamp(k.last_used_at)}`
                  : " · never used"}
              </p>
            </div>
            <div className={chrome.rowActionsWide}>
              <span className={cx(badge.badge, badge.green)}>
                Active
              </span>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setConfirmRevoke(k)}
              >
                Revoke
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {/* The panel sits below the list it appends to (DESIGN.md), and
          keeps its submit button: minting a credential is committing a new
          entity, not saving a setting. */}
      {formOpen && (
        <div className={chrome.panel}>
          <label className={forms.label} htmlFor="api-key-name">
            Name <span className={forms.optional}>(optional)</span>
          </label>
          <input
            id="api-key-name"
            type="text"
            autoFocus
            className={forms.input}
            value={name}
            disabled={creating}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
            }}
          />
          {createError && (
            <InlineBanner variant="error">{createError}</InlineBanner>
          )}
          <div className={chrome.panelActions}>
            <Button size="sm" loading={creating} onClick={() => void create()}>
              Create key
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setFormOpen(false);
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmRevoke !== null}
        title={`Revoke ${confirmRevoke?.name || "this key"}?`}
        message="Anything using it stops working immediately. This cannot be undone."
        confirmLabel="Revoke"
        confirmVariant="danger"
        error={revokeError}
        loading={revoking}
        onConfirm={doRevoke}
        onCancel={() => {
          setConfirmRevoke(null);
          setRevokeError(null);
        }}
      />
    </>
  );
}
